import type { Editor, EditorOptions, Extensions } from '@tiptap/core';
import { useEditor } from '@tiptap/react';
import StarterKit, { type StarterKitOptions } from '@tiptap/starter-kit';
import { useRef } from 'react';
import type { IMergeFieldToken } from './mergeFieldSerialization';
import { MergeField } from './mergeFieldToken';

export interface IRichTextChangeMeta {
	isEmpty: boolean;
}

export interface IUseRichTextEditorOptions {
	initialContent: EditorOptions['content'];
	starterKitOptions?: Partial<StarterKitOptions>;
	extensions?: Extensions;
	mergeField?:
		| false
		| {
				tokens?: readonly IMergeFieldToken[];
		  };
	editorProps?: EditorOptions['editorProps'];
	onCreate?: (editor: Editor) => void;
	onUpdate?: (editor: Editor, meta: IRichTextChangeMeta) => void;
	onBlur?: (editor: Editor) => void;
	onDestroy?: (editor: Editor) => void;
	immediatelyRender?: boolean;
	shouldRerenderOnTransaction?: boolean;
}

interface IInitialEditorOptions {
	content: EditorOptions['content'];
	editorProps?: EditorOptions['editorProps'];
	extensions: Extensions;
	immediatelyRender?: boolean;
	shouldRerenderOnTransaction?: boolean;
}

export const useRichTextEditor = ({
	initialContent,
	starterKitOptions,
	extensions = [],
	mergeField = false,
	editorProps,
	onCreate,
	onUpdate,
	onBlur,
	onDestroy,
	immediatelyRender,
	shouldRerenderOnTransaction,
}: IUseRichTextEditorOptions): Editor | null => {
	const callbacksRef = useRef({ onBlur, onCreate, onDestroy, onUpdate });
	callbacksRef.current = { onBlur, onCreate, onDestroy, onUpdate };
	const editorRef = useRef<Editor | null>(null);

	const initialOptionsRef = useRef<IInitialEditorOptions | null>(null);
	if (!initialOptionsRef.current) {
		initialOptionsRef.current = {
			content: initialContent,
			editorProps,
			extensions: [
				StarterKit.configure(starterKitOptions ?? {}),
				...(mergeField ? [MergeField.configure({ tokens: [...(mergeField.tokens ?? [])] })] : []),
				...extensions,
			],
			immediatelyRender,
			shouldRerenderOnTransaction,
		};
	}

	const initialOptions = initialOptionsRef.current;
	return useEditor({
		content: initialOptions.content,
		extensions: initialOptions.extensions,
		...(initialOptions.editorProps ? { editorProps: initialOptions.editorProps } : {}),
		...(initialOptions.immediatelyRender === undefined ? {} : { immediatelyRender: initialOptions.immediatelyRender }),
		...(initialOptions.shouldRerenderOnTransaction === undefined
			? {}
			: { shouldRerenderOnTransaction: initialOptions.shouldRerenderOnTransaction }),
		onCreate: ({ editor }) => {
			editorRef.current = editor;
			callbacksRef.current.onCreate?.(editor);
		},
		onUpdate: ({ editor }) => callbacksRef.current.onUpdate?.(editor, { isEmpty: editor.isEmpty }),
		onBlur: ({ editor }) => callbacksRef.current.onBlur?.(editor),
		onDestroy: () => {
			const editor = editorRef.current;
			if (editor) callbacksRef.current.onDestroy?.(editor);
			editorRef.current = null;
		},
	});
};
